import { debug } from 'application/Shared/Utils';

import { HeapUserProperties, PendoUserProperties } from '../Constants';

// constants
const CHECK_LIBRARY_INTERVAL_MS = 50;
const CHECK_LIBRARY_MAX_ATTEMPTS_MS = 100;

const getUserProperties = (session = {}, isPendo = false) => {
    const keySyntaxObject = isPendo ? PendoUserProperties : HeapUserProperties;
    const { user, sessionSettings, tenant } = session;

    return {
        [keySyntaxObject.EMAIL]: user?.email,
        [keySyntaxObject.ID]: user?.id,
        [keySyntaxObject.FIRST_NAME]: user?.firstName,
        [keySyntaxObject.LAST_NAME]: user?.lastName,
        [keySyntaxObject.AUVIK_DOMAIN]: user?.auvikDomain,
        [keySyntaxObject.PERMISSION_ID]: user?.permission?.id,
        [keySyntaxObject.PERMISSION_NAME]: user?.permission?.name,
        [keySyntaxObject.TENANT_CREATION_DATE]: tenant?.dateCreated,
        [keySyntaxObject.TENANT_IDENTITY_ID]: tenant?.identityTenantId,
        [keySyntaxObject.TENANT_IDENTITY_NAME]: tenant?.identityTenantName,
        [keySyntaxObject.TENANT_IDENTITY_TYPE]: tenant?.identityTenantType,
        [keySyntaxObject.TENANT_NAME]: tenant?.name,
        [keySyntaxObject.TENANT_REGION]: tenant?.region,
        [keySyntaxObject.TENANT_ANM]: tenant?.anm,
        [keySyntaxObject.HUB_TYPE]: sessionSettings?.hubType,
        [keySyntaxObject.LICENSE_HUB]: sessionSettings?.licensedHub,
        [keySyntaxObject.IN_TRIAL]: sessionSettings?.inTrial,
        [keySyntaxObject.EXPIRED_TRIAL]: sessionSettings?.expiredTrial
    };
};

// populate heap user properties with values from user session
const addUserDataToHeap = async (session) => {
    // first ensure the heap library has loaded
    const waitForHeap = () =>
        new Promise((resolve, reject) => {
            if (window?.heap?.loaded) resolve();
            // check if library has loaded every 50ms (normally take less than 100ms)
            let attempts = 0;
            const intervalId = setInterval(() => {
                if (window?.heap?.loaded) {
                    clearInterval(intervalId);
                    resolve();
                }
                attempts += 1;
                // but only try for up to ~5 seconds
                if (attempts >= CHECK_LIBRARY_MAX_ATTEMPTS_MS) {
                    clearInterval(intervalId);
                    reject();
                }
            }, CHECK_LIBRARY_INTERVAL_MS);
        });

    if (process.env.REACT_APP_ENABLE_HEAP === 'true') {
        waitForHeap()
            .then(() => {
                // once library loaded, add user properties
                const { heap } = window;
                if (heap && session) {
                    try {
                        heap.addUserProperties(getUserProperties(session, false));
                    } catch (e) {
                        debug.e('Error adding user properties to Heap', e);
                    }
                }
            })
            .catch(() => {
                debug.e('Heap library failed to load within the timeout period.');
            });
    }
};

const getPendoOptions = (session) => ({
    account: {
        id: session?.tenant?.id,
        name: session?.tenant?.name
    },
    visitor: {
        email: session?.user?.email,
        id: session?.id,
        ...getUserProperties(session, true)
    }
});

// updating any session changes after pendo is already initialized
const updatePendo = (session, isInitialized) => {
    const { pendo } = window;
    if (pendo && session) {
        try {
            const options = getPendoOptions(session);
            if (isInitialized) {
                pendo.updateOptions(options);
            } else {
                pendo.initialize(options);
            }
        } catch (e) {
            debug.e(
                isInitialized
                    ? 'Error updating pendo options'
                    : 'Error initializing pendo',
                e
            );
        }
    }
};

// initialize pendo with session properties
const initializePendo = async (session) => {
    const waitForPendo = () =>
        new Promise((resolve, reject) => {
            // check if library has loaded every 50ms (normally take less than 100ms)
            let attempts = 0;
            const intervalId = setInterval(() => {
                if (window?.pendo?.getVersion && window.pendo.getVersion()) {
                    clearInterval(intervalId);
                    resolve();
                }
                attempts += 1;
                // but only try for up to ~5 seconds
                if (attempts >= CHECK_LIBRARY_MAX_ATTEMPTS_MS) {
                    clearInterval(intervalId);
                    reject();
                }
            }, CHECK_LIBRARY_INTERVAL_MS);
        });

    waitForPendo()
        .then(() => {
            updatePendo(session, false);
        })
        .catch(() => {
            debug.e('Pendo library failed to load within the timeout period.');
        });
};

const addUserDataToPendo = (session) => {
    const pendoNeedsToBeInitialized =
        typeof window.pendo?.apiKey === 'undefined';
    if (pendoNeedsToBeInitialized) {
        initializePendo(session);
    } else {
        updatePendo(session, true);
    }
};

const loadPendo = (pendoID) => {
    // eslint-disable-next-line
    (function(apiKey){(function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];v=['initialize','identify','updateOptions','pageLoad'];for(w=0,x=v.length;w<x;++w)(function(m){o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');})(pendoID);
};

const loadHeap = (heapID) => {
    // eslint-disable-next-line
    window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};heap.load(heapID);
};

const addUserTrackingScripts = () => {
    const enableHeap =
        typeof window.heap === 'undefined' &&
        process.env.REACT_APP_ENABLE_HEAP === 'true';

    if (enableHeap) {
        const heapID = process.env.REACT_APP_HEAP_ID;
        loadHeap(heapID);
    }

    const enablePendo =
        typeof window.pendo === 'undefined' &&
        process.env.REACT_APP_ENABLE_PENDO === 'true';

    if (enablePendo) {
        const pendoID = process.env.REACT_APP_PENDO_ID;
        loadPendo(pendoID);
    }
};

export { addUserDataToHeap, addUserDataToPendo, addUserTrackingScripts };
